// src/polaris-components/CancelSubscriptionModal.tsx
import React, { useState } from 'react';
import { cancelSubscription, reactivateSubscription, CancelSubscriptionResponse } from '../utils/subscriptionApi';
import '../styles/cancel-subscription-modal.css';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentPeriodEnd?: string;
}

export const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  currentPeriodEnd
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelResult, setCancelResult] = useState<CancelSubscriptionResponse | null>(null);
  const [reactivateResult, setReactivateResult] = useState<CancelSubscriptionResponse | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const handleCancel = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await cancelSubscription();
      setCancelResult(result);
      // Don't call onSuccess() here - wait until user closes the success modal
    } catch (err: any) {
      if (err.status === 401) {
        setError('You are not authenticated. Please log in again.');
      } else if (err.status === 404) {
        setError('Subscription not found.');
      } else if (err.status === 500) {
        setError('Unable to cancel subscription. Please try again later.');
      } else {
        setError(err.message || 'Failed to cancel subscription. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async () => {
    setIsReactivating(true);
    setError(null);

    try {
      const result = await reactivateSubscription();
      setReactivateResult(result);
      // Clear cancel result to show reactivation success
      setCancelResult(null);
    } catch (err: any) {
      if (err.status === 401) {
        setError('You are not authenticated. Please log in again.');
      } else if (err.status === 404) {
        setError('Subscription not found.');
      } else if (err.status === 500) {
        setError('Unable to reactivate subscription. Please try again later.');
      } else {
        setError(err.message || 'Failed to reactivate subscription. Please try again.');
      }
    } finally {
      setIsReactivating(false);
    }
  };

  const handleClose = () => {
    const wasSuccessful = cancelResult !== null || reactivateResult !== null;
    setCancelResult(null);
    setReactivateResult(null);
    setError(null);
    onClose();

    // Only refetch data after showing success and user closes modal
    if (wasSuccessful) {
      onSuccess();
    }
  };

  if (!isOpen) return null;

  // Show reactivation success state
  if (reactivateResult) {
    return (
      <div className="modal-overlay" data-testid="cancel-subscription-modal">
        <div className="modal-content cancel-success-modal">
          <div className="success-icon">✓</div>
          <h2>Subscription Reactivated</h2>
          <p className="success-message" data-testid="reactivate-success-message">
            {reactivateResult.message}
          </p>
          <p className="effective-date" data-testid="reactivate-effective-date">
            Your subscription will continue and you won't lose access.
          </p>
          <div className="modal-actions">
            <button
              onClick={handleClose}
              className="modal-btn reactivate-btn"
              data-testid="close-reactivate-modal-btn"
            >
              Great! Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show cancellation success state
  if (cancelResult) {
    return (
      <div className="modal-overlay" data-testid="cancel-subscription-modal">
        <div className="modal-content cancel-success-modal">
          <div className="success-icon">✓</div>
          <h2>Subscription Cancelled</h2>
          <p className="success-message" data-testid="cancel-success-message">
            {cancelResult.message}
          </p>
          <p className="effective-date" data-testid="effective-date">
            Your subscription will end on <strong>{formatDate(cancelResult.effectiveDate)}</strong>
          </p>

          {error && (
            <div className="modal-error" data-testid="reactivate-error">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button
              onClick={handleReactivate}
              className="modal-btn reactivate-btn"
              disabled={isReactivating}
              data-testid="reactivate-btn"
            >
              {isReactivating ? (
                <>
                  <span className="btn-spinner"></span>
                  Reactivating...
                </>
              ) : (
                'Change Your Mind? Reactivate Subscription'
              )}
            </button>
            <button
              onClick={handleClose}
              className="modal-btn secondary-btn"
              disabled={isReactivating}
              data-testid="close-modal-btn"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show confirmation dialog
  return (
    <div className="modal-overlay" data-testid="cancel-subscription-modal">
      <div className="modal-content cancel-confirmation-modal">
        <button
          className="modal-close-icon"
          onClick={handleClose}
          aria-label="Close modal"
          data-testid="close-icon-btn"
        >
          ×
        </button>
        <h2>Cancel Subscription?</h2>
        <p className="confirmation-message" data-testid="confirmation-message">
          Are you sure you want to cancel your subscription?
          {currentPeriodEnd && (
            <> You'll keep access until <strong>{formatDate(currentPeriodEnd)}</strong>.</>
          )}
        </p>

        {error && (
          <div className="modal-error" data-testid="cancel-error">
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button
            onClick={handleClose}
            className="modal-btn secondary-btn"
            disabled={isLoading}
            data-testid="keep-subscription-btn"
          >
            Keep Subscription
          </button>
          <button
            onClick={handleCancel}
            className="modal-btn danger-btn"
            disabled={isLoading}
            data-testid="confirm-cancel-btn"
          >
            {isLoading ? (
              <>
                <span className="btn-spinner"></span>
                Cancelling...
              </>
            ) : (
              'Yes, Cancel Subscription'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

